/**
 * INCSUBResetPass Component
 * 
 * This component allows users to reset their password by entering a new password
 * and confirming it. The password must meet specific criteria for security.
 * 
 * Features:
 * - Real-time password validation
 * - Feedback on mismatched or invalid passwords
 * - Submits updated password to the server
 * - Handles various response scenarios (success, errors, network issues)
 * 
 * Props:
 * - data (object): Contains user data such as mobile number and OTP required for resetting the password.
 * 
 * Dependencies:
 * - `react-toastify` for notifications
 * - `react-router` for navigation
 * - `updatePassword` service function for API calls
 * @author govind dindewad
 */

import React, { useState } from 'react';
import './styleResetPass.css';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router';
import { updatePassword } from '../../../services/authService';

function INCSUBResetPass({ data }) {
    // State to manage user input for password and confirmation
    const [loginInfo, setLoginInfo] = useState({
        password: "",
        cnfpassword: ""
    });

    const navigate = useNavigate();
    
    // State to handle error highlighting for invalid password inputs
    const [passErr, setPassErr] = useState();
    const [cnfPassErr, setCnfPassErr] = useState();

    /**
     * Updates the password field in the state and clears password error.
     * @param {Object} e - Event object from input field.
     * @author govind dindewad
     */
    const handlePassword = (e) => {
        setLoginInfo({
            ...loginInfo, // Preserve other fields
            password: e.target.value // Update the password field
        });
        setPassErr(false); // Clear previous error
    };

    /**
     * Updates the confirm password field in the state and clears confirm password error.
     * @param {Object} e - Event object from input field.
     * @author govind dindewad
     */
    const handlecnfPassword = (e) => {
        setLoginInfo({
            ...loginInfo, // Preserve other fields
            cnfpassword: e.target.value // Update the confirm password field
        });
        setCnfPassErr(false); // Clear previous error
    };

    /**
     * Validates if the password meets the specified criteria.
     * @param {string} password - The password to validate.
     * @returns {boolean} - Returns true if the password is valid, false otherwise.
     * @author govind dindewad
     */
    function isValidPassword(password) {
        const regex = /^(?=.*\d)(?=.*[a-z])(?=.*[A-Z])(?=.*[!@#$&*]).{8,20}$/;
        return regex.test(password);
    }

    /**
     * Handles submission of new password.
     * Validates inputs, sends API request, and navigates on success or displays appropriate error messages.
     * @author govind dindewad
     */
    const handleNewPassword = async () => {

        // Validate password criteria
        if (!isValidPassword(loginInfo.password)) {
            toast.warning('Password length should be in between 8 to 20 characters string & must contain at least one digit, one upper case letter, one lower case letter & one special symbol. Among this symbol(!@#$&*)');
            setPassErr(true);
            return;
        }

        // Validate confirm password match
        if (loginInfo.password !== loginInfo.cnfpassword) {
            toast.warning('Confirm password does not match the new password.');
            setCnfPassErr(true);
            return;
        }

        // Clear previous errors
        setPassErr(false);
        setCnfPassErr(false);

        // Prepare data for API request
        const updatedData = {
            userMobileNumber: data.userMobileNumber,
            userPassword: loginInfo.password,
            otp: data.otp
        };

        // API request to update the password
        try {
            const response = await updatePassword(updatedData);
            if (response?.status === 200) {
                toast.success("Password Changed Successfully");
                navigate('/login');
            } else if (response?.status === 301) {
                toast.error('Network Error');
            } else if (response?.status === 500) {
                toast.error('Internal Server Error');
            } else if (response?.status === 401) {
                navigate("/login");
            } else {
                toast.success(response?.data.message);
            }
        } catch (err) {
            toast.error("Something Went Wrong");
        }
    };

    return (
        <div className='INCSUBResetPasspage'>
            <div className='INCSUBResetPasspage_container'>
                <div className='INCSUBResetPassinfo_container'>
                    <div className='INCSUBResetPasspage_headerinfo_container'>
                        <div className='INCSUBResetPasspage_header'>
                            Set New Password
                        </div>
                        <div className='INCSUBResetPasspage_subheader'>Your new password must be different to previously used passwords.</div>
                    </div>
                    <div className='INCSUBResetPassForm_container'>
                        <form className='INCSUBResetPass_form' onSubmit={(e) => {
                            e.preventDefault();
                            handleNewPassword();
                        }}>
                            <div className='INCSUBUsercred_container'>
                                <label>New Password</label>
                                <input 
                                    type='text' 
                                    name='password' 
                                    placeholder='Enter Password' 
                                    value={loginInfo.password} 
                                    onChange={handlePassword} 
                                    style={{ border: passErr ? "1px solid red" : null }} 
                                />
                                <p>Password length should be in between 8 to 20 characters string & must contain at least one digit, one upper case letter, one lower case letter & one special symbol. Among this symbol(!@#$&*)</p>
                            </div>
                            <div className='INCSUBUsercred_container'>
                                <label>Confirm Password</label>
                                <input 
                                    type='text' 
                                    name='confirm_password' 
                                    placeholder='Enter confirm Password' 
                                    value={loginInfo.cnfpassword} 
                                    onChange={handlecnfPassword} 
                                    style={{ border: cnfPassErr ? "1px solid red" : null }} 
                                />
                            </div>
                            <input 
                                type='button' 
                                placeholder='Set New Password' 
                                value={"Change Password"} 
                                className='INCSUBResetPassForm_submitbutton' 
                                onClick={(e) => {
                                    e.preventDefault();
                                    handleNewPassword();
                                }}
                            />
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default INCSUBResetPass;
